import React from 'react';
import styled from 'styled-components';
// Imported from the leaf module, NOT the @ui barrel: src/ui/index.js re-exports ./inputs, and
// src/ui/text/Body4.js imports from @ui, so going through the barrel here closes a require cycle
// that leaves Body7 undefined for whichever module loads first.
import { Body7 } from '@ui/text/Body7';

// Labelled select. `options` is `[{ value, label, disabled?, reason? }]`; a disabled option keeps
// its reason in the label so the operator can see WHY a choice is unavailable rather than watching
// it silently not exist.

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Field = styled.select`
  width: 100%;
  height: 38px;
  border: 1px solid ${({ theme, $invalid }) => ($invalid ? theme.themeColors.error : theme.themeColors.border)};
  border-radius: 6px;
  padding: 0 9px;
  background: transparent;
  color: ${({ theme }) => theme.themeColors.text};
  font-size: 14px;
  font-family: inherit;
  cursor: pointer;

  &:disabled {
    background: ${({ theme }) => theme.themeColors.disabled};
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    outline-offset: 1px;
  }

  option {
    color: ${({ theme }) => theme.themeColors.text};
    background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  }
`;

const Hint = styled(Body7)`
  color: ${({ theme, $invalid }) => ($invalid ? theme.themeColors.error : theme.themeColors.secondaryText)};
`;

let uid = 0;
const nextId = () => {
  uid += 1;
  return `select-field-${uid}`;
};

export const Select = ({ label, value, onChange, options = [], hint, invalid = false, placeholder, id, ...rest }) => {
  const [fallbackId] = React.useState(nextId);
  const fieldId = id || fallbackId;
  const hintId = hint ? `${fieldId}-hint` : undefined;

  return (
    <Wrapper>
      {label && (
        <label htmlFor={fieldId}>
          <Body7>{label}</Body7>
        </label>
      )}
      <Field
        id={fieldId}
        value={value === undefined || value === null ? '' : value}
        onChange={(e) => onChange && onChange(e.target.value)}
        aria-describedby={hintId}
        aria-invalid={invalid || undefined}
        $invalid={invalid}
        {...rest}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.disabled && option.reason ? `${option.label} — ${option.reason}` : option.label}
          </option>
        ))}
      </Field>
      {hint && (
        <Hint id={hintId} $invalid={invalid}>
          {hint}
        </Hint>
      )}
    </Wrapper>
  );
};
